import React, { useState, useRef, useEffect } from 'react';
import {
    X,
    SkipBack,
    SkipForward,
    Pause,
    Play,
    Gear
} from '@phosphor-icons/react';
import { Button } from './button';
import { cn } from '@/lib/utils';

export interface AudioItem {
    section: string;
    url: string;
}

interface AudioPlayerProps {
    audios: AudioItem[];
    isVisible?: boolean;
    onClose?: () => void;
    onSettings?: () => void;
}

export function AudioPlayer({
    audios,
    isVisible = true,
    onClose,
    onSettings
}: AudioPlayerProps) {
    const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const audioRef = useRef<HTMLAudioElement>(null);

    const currentAudio = audios[currentTrackIndex];

    useEffect(() => {
        if (audioRef.current) {
            const audio = audioRef.current;

            const updateTime = () => {
                if (!isNaN(audio.currentTime)) {
                    setCurrentTime(audio.currentTime);
                }
            };

            const updateDuration = () => {
                console.log('Audio duration loaded:', audio.duration);
                if (!isNaN(audio.duration) && audio.duration > 0 && audio.duration !== Infinity) {
                    setDuration(audio.duration);
                }
            };

            const handleLoadStart = () => {
                console.log('Audio load started for:', currentAudio?.url);
                setIsLoading(true);
                setError(null);
            };

            const handleCanPlay = () => {
                console.log('Audio can play, duration:', audio.duration);
                setIsLoading(false);
                updateDuration();
            };

            const handleLoadedMetadata = () => {
                console.log('Audio metadata loaded, duration:', audio.duration);
                updateDuration();
            };

            const handleLoadedData = () => {
                console.log('Audio data loaded, duration:', audio.duration);
                updateDuration();
            };

            const handleCanPlayThrough = () => {
                console.log('Audio can play through, duration:', audio.duration);
                updateDuration();
            };

            const handleError = (e: Event) => {
                console.error('Audio error event:', e);
                console.error('Audio error details:', audio.error);
                setIsLoading(false);
                setError('Failed to load audio');
            };

            const handleEnded = () => {
                if (currentTrackIndex < audios.length - 1) {
                    setCurrentTrackIndex(prev => prev + 1);
                    // Keep playing state true to automatically start next track
                } else {
                    // If it's the last track, stop playing
                    setIsPlaying(false);
                }
            };

            audio.addEventListener('timeupdate', updateTime);
            audio.addEventListener('loadedmetadata', handleLoadedMetadata);
            audio.addEventListener('loadeddata', handleLoadedData);
            audio.addEventListener('canplay', handleCanPlay);
            audio.addEventListener('canplaythrough', handleCanPlayThrough);
            audio.addEventListener('loadstart', handleLoadStart);
            audio.addEventListener('error', handleError);
            audio.addEventListener('ended', handleEnded);

            return () => {
                audio.removeEventListener('timeupdate', updateTime);
                audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
                audio.removeEventListener('loadeddata', handleLoadedData);
                audio.removeEventListener('canplay', handleCanPlay);
                audio.removeEventListener('canplaythrough', handleCanPlayThrough);
                audio.removeEventListener('loadstart', handleLoadStart);
                audio.removeEventListener('error', handleError);
                audio.removeEventListener('ended', handleEnded);
            };
        }
    }, [currentTrackIndex, audios.length]);

    useEffect(() => {
        if (audioRef.current && currentAudio) {
            console.log('Loading audio:', currentAudio.url);
            setError(null);
            setIsLoading(true);
            setDuration(0); // Reset duration when loading new audio
            audioRef.current.src = currentAudio.url;
            audioRef.current.load(); // Explicitly load the audio

            // Add a timeout to check duration after loading
            const durationCheck = setTimeout(() => {
                if (audioRef.current && audioRef.current.duration > 0 && !isNaN(audioRef.current.duration)) {
                    console.log('Duration check timeout - setting duration:', audioRef.current.duration);
                    setDuration(audioRef.current.duration);
                } else {
                    console.log('Duration still not available after timeout, retrying...');
                    // Try to reload the audio to get duration
                    if (audioRef.current) {
                        audioRef.current.load();
                    }
                }
            }, 2000);

            // Add a second check after 5 seconds
            const secondDurationCheck = setTimeout(() => {
                if (audioRef.current && audioRef.current.duration > 0 && !isNaN(audioRef.current.duration)) {
                    console.log('Second duration check - setting duration:', audioRef.current.duration);
                    setDuration(audioRef.current.duration);
                } else {
                    console.log('Duration still not available after second timeout');
                }
            }, 5000);

            // Always try to play when track changes, regardless of isPlaying state
            // This ensures automatic progression works
            audioRef.current.play().catch((err) => {
                console.error('Autoplay failed:', err);
                // If autoplay fails, respect the isPlaying state
                if (!isPlaying) {
                    audioRef.current?.pause();
                }
            });

            return () => {
                clearTimeout(durationCheck);
                clearTimeout(secondDurationCheck);
            };
        }
    }, [currentTrackIndex, currentAudio]);

    const togglePlayPause = () => {
        if (audioRef.current) {
            if (isPlaying) {
                audioRef.current.pause();
            } else {
                audioRef.current.play().catch((err) => {
                    console.error('Play failed:', err);
                    setError('Failed to play audio');
                });
            }
            setIsPlaying(!isPlaying);
        }
    };

    const playPrevious = () => {
        if (currentTrackIndex > 0) {
            setCurrentTrackIndex(prev => prev - 1);
        }
    };

    const playNext = () => {
        if (currentTrackIndex < audios.length - 1) {
            setCurrentTrackIndex(prev => prev + 1);
        }
    };

    const formatTime = (time: number) => {
        if (isNaN(time) || time === Infinity) {
            return '0:00';
        }
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (audioRef.current && duration > 0 && !isNaN(duration)) {
            const rect = e.currentTarget.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const percentage = clickX / rect.width;
            const newTime = percentage * duration;
            audioRef.current.currentTime = newTime;
            setCurrentTime(newTime);
        }
    };

    // Calculate progress percentage safely
    const progressPercentage = duration > 0 && !isNaN(duration) ? (currentTime / duration) * 100 : 0;

    // Show duration status for debugging
    const durationStatus = duration > 0 ? 'Duration loaded' : 'Duration not available';

    if (!isVisible || audios.length === 0) return null;

    return (
        <>
            <audio ref={audioRef} preload="metadata" />

            {/* Floating Audio Player */}
            <div className="fixed bottom-0 left-0 right-0 z-50 bg-background-secondary border-t border-celestial-moon/20 shadow-lg">
                <div className="max-w-md mx-auto p-4">
                    {/* Progress Bar */}
                    <div className="flex items-center gap-3 mb-3">
                        <span className="text-xs text-primary font-medium min-w-[2.5rem] font-sans">
                            {formatTime(currentTime)}
                        </span>

                        <div
                            className="flex-1 h-1.5 bg-celestial-moon/30 rounded-full cursor-pointer relative"
                            onClick={handleProgressClick}
                        >
                            <div
                                className="h-full bg-celestial-darkBlue rounded-full transition-all duration-100"
                                style={{ width: `${progressPercentage}%` }}
                            />
                            {duration > 0 && !isNaN(duration) && (
                                <div
                                    className="absolute top-1/2 transform -translate-y-1/2 w-3 h-3 bg-celestial-darkBlue rounded-full -ml-1.5 cursor-pointer shadow-sm"
                                    style={{ left: `${progressPercentage}%` }}
                                />
                            )}
                        </div>

                        <span className="text-xs text-primary font-medium min-w-[2.5rem] font-sans">
                            {duration > 0 && !isNaN(duration) ? `-${formatTime(duration - currentTime)}` : '--:--'}
                        </span>
                    </div>



                    {/* Error Message */}
                    {error && (
                        <div className="text-xs text-red-500 text-center mb-2">
                            {error}
                        </div>
                    )}

                    {/* Controls */}
                    <div className="flex items-center justify-between mb-2">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={onClose}
                            className="h-8 w-8 text-gray-700 hover:bg-pink-200"
                        >
                            <X className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={playPrevious}
                            disabled={currentTrackIndex === 0}
                            className="h-9 w-9 text-celestial-darkBlue hover:bg-celestial-moon/20 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            <SkipBack size={18} weight="regular" />
                        </Button>

                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={togglePlayPause}
                            disabled={isLoading || !!error}
                            className="h-12 w-12 text-celestial-darkBlue hover:bg-celestial-moon/20 bg-celestial-moon/10 rounded-full disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {isLoading ? (
                                <div className="w-5 h-5 border-2 border-celestial-darkBlue border-t-transparent rounded-full animate-spin" />
                            ) : isPlaying ? (
                                <Pause size={20} weight="regular" />
                            ) : (
                                <Play size={20} weight="regular" />
                            )}
                        </Button>

                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={playNext}
                            disabled={currentTrackIndex === audios.length - 1}
                            className="h-9 w-9 text-celestial-darkBlue hover:bg-celestial-moon/20 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            <SkipForward size={18} weight="regular" />
                        </Button>

                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={onSettings}
                            className="h-9 w-9 text-celestial-darkBlue hover:bg-celestial-moon/20 disabled:opacity-20 disabled:cursor-not-allowed"
                        >
                            <Gear size={18} weight="regular" />
                        </Button>
                    </div>

                    {/* Track Info */}
                    {currentAudio && (
                        <div className="text-center">
                            <p className="text-sm text-primary font-medium truncate font-serif">
                                {currentAudio.section}
                            </p>
                            <p className="text-xs text-secondary font-sans">
                                {currentTrackIndex + 1} of {audios.length}
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Bottom padding to prevent content from being hidden behind the player */}
            <div className="h-36" />
        </>
    );
} 